import type {
  ApiResponse,
  PublicSessionArtifactDescriptor,
  SessionArtifactManifestApiResponse,
  SessionChannelGroupArtifactV2,
  SessionGeometryArtifactV2,
  SessionLapsArtifactV2,
  SessionOverviewArtifactV2,
} from '@kangaroo/shared';
import axios from 'axios';

import { api } from '../../lib/api';
import type { MyChronSessionExport } from '../../lib/telemetrySession';

export type SessionArtifactView = 'map' | 'traces';

const DB_NAME = 'paddiq-session-artifact-cache';
const DB_VERSION = 1;
const STORE_NAME = 'artifacts';
const MANIFEST_TTL_MS = 30_000;
const artifactMemory = new Map<string, unknown>();
const manifestMemory = new Map<string, { savedAt: number; value: SessionArtifactManifestApiResponse }>();
type SharedRequest<T> = {
  controller: AbortController;
  promise: Promise<T>;
  consumers: number;
};
const artifactRequests = new Map<string, SharedRequest<unknown>>();
const manifestRequests = new Map<string, SharedRequest<SessionArtifactManifestApiResponse>>();

// A view owns its subscription, not a transport shared with other views.
function consumeRequest<T>(request: SharedRequest<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(new axios.CanceledError());
  request.consumers += 1;
  return new Promise<T>((resolve, reject) => {
    let finished = false;
    const finish = (callback: () => void) => {
      if (finished) return;
      finished = true;
      signal.removeEventListener('abort', cancel);
      request.consumers -= 1;
      if (request.consumers === 0) request.controller.abort();
      callback();
    };
    const cancel = () => finish(() => reject(new axios.CanceledError()));
    signal.addEventListener('abort', cancel, { once: true });
    request.promise.then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error)),
    );
  });
}
const prefetchRequests = new Map<string, Promise<void>>();
const activeRequests = new Map<string, AbortController>();

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Artifact cache open failed'));
  });
}

async function readDisk(hash: string) {
  if (typeof indexedDB === 'undefined') return null;
  const db = await openDb();
  try {
    return await new Promise<unknown | null>((resolve, reject) => {
      const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(hash);
      request.onsuccess = () => resolve((request.result as unknown | undefined) ?? null);
      request.onerror = () => reject(request.error ?? new Error('Artifact cache read failed'));
    });
  } finally {
    db.close();
  }
}

async function writeDisk(hash: string, value: unknown) {
  if (typeof indexedDB === 'undefined') return;
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(value, hash);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Artifact cache write failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Artifact cache write aborted'));
    });
  } finally {
    db.close();
  }
}

async function fetchManifest(sessionId: string, signal: AbortSignal) {
  if (signal.aborted) throw new axios.CanceledError();
  const cached = manifestMemory.get(sessionId);
  if (cached && Date.now() - cached.savedAt < MANIFEST_TTL_MS) return cached.value;
  const inFlight = manifestRequests.get(sessionId);
  if (inFlight && !inFlight.controller.signal.aborted) return consumeRequest(inFlight, signal);
  const controller = new AbortController();
  const request: SharedRequest<SessionArtifactManifestApiResponse> = {
    controller,
    consumers: 0,
    promise: api.get<ApiResponse<SessionArtifactManifestApiResponse>>(
      `/data-files/${encodeURIComponent(sessionId)}/artifacts/manifest`,
      { signal: controller.signal },
    ).then((response) => {
      if (controller.signal.aborted) throw new axios.CanceledError();
      manifestMemory.set(sessionId, { savedAt: Date.now(), value: response.data.data });
      return response.data.data;
    }).finally(() => {
      if (manifestRequests.get(sessionId) === request) manifestRequests.delete(sessionId);
    }),
  };
  manifestRequests.set(sessionId, request);
  return consumeRequest(request, signal);
}

async function fetchArtifact<T>(descriptor: PublicSessionArtifactDescriptor, signal: AbortSignal): Promise<T> {
  if (signal.aborted) throw new axios.CanceledError();
  if (artifactMemory.has(descriptor.sha256)) return artifactMemory.get(descriptor.sha256) as T;
  const inFlight = artifactRequests.get(descriptor.sha256);
  if (inFlight && !inFlight.controller.signal.aborted) return consumeRequest(inFlight, signal) as Promise<T>;
  const controller = new AbortController();
  const request: SharedRequest<unknown> = {
    controller,
    consumers: 0,
    promise: (async () => {
      const persisted = await readDisk(descriptor.sha256).catch(() => null);
      if (controller.signal.aborted) throw new axios.CanceledError();
      if (persisted !== null) {
        artifactMemory.set(descriptor.sha256, persisted);
        return persisted;
      }
      const requestUrl = descriptor.downloadUrl.replace(/^\/api\/v1(?=\/)/, '');
      const response = await api.get<T>(requestUrl, { signal: controller.signal });
      if (controller.signal.aborted) throw new axios.CanceledError();
      artifactMemory.set(descriptor.sha256, response.data);
      void writeDisk(descriptor.sha256, response.data).catch(() => undefined);
      return response.data;
    })().finally(() => {
      if (artifactRequests.get(descriptor.sha256) === request) artifactRequests.delete(descriptor.sha256);
    }),
  };
  artifactRequests.set(descriptor.sha256, request);
  return consumeRequest(request, signal) as Promise<T>;
}

function assembleSession(
  overview: SessionOverviewArtifactV2,
  laps: SessionLapsArtifactV2,
  geometry: SessionGeometryArtifactV2 | null,
  channelGroups: SessionChannelGroupArtifactV2[],
): MyChronSessionExport {
  const series: MyChronSessionExport['series'] = {};
  for (const group of channelGroups) {
    for (const channel of group.channels) {
      series[channel.shortName] = channel.samples.map(([timecodeMs, value]) => ({ timecodeMs, value }));
    }
  }
  return {
    format: overview.format,
    channels: overview.channels.map((channel) => ({
      index: channel.index,
      shortName: channel.shortName,
      longName: channel.longName,
      unit: channel.unit,
    })),
    laps: laps.raw.laps.map((lap) => ({
      segment: Number(lap.lapId.split('-')[1] ?? 0),
      lapNumber: lap.lapNumber,
      durationMs: lap.durationMs,
      endTimeMs: lap.endMs,
    })),
    gps: (geometry?.sessionPath ?? []).map((point) => ({
      timecodeMs: point.timeMs ?? 0,
      lat: point.lat,
      lon: point.lon,
      alt: point.altitudeM ?? 0,
      speed: point.speedMps ?? 0,
    })),
    series,
    metadata: {
      ...(overview.metadata.driver ? { driver: overview.metadata.driver } : {}),
      ...(overview.metadata.vehicle ? { vehicle: overview.metadata.vehicle } : {}),
      ...(overview.metadata.venue ? { venue: overview.metadata.venue } : {}),
    },
    stats: {
      gpsSamples: overview.counts.gpsSamples,
      singleSamples: overview.channels.reduce((sum, channel) => sum + channel.sampleCount, 0),
      ...(overview.recordedAtMs !== null ? { recordedAtMs: overview.recordedAtMs } : {}),
    },
  };
}

export async function fetchSessionArtifactExport(
  sessionId: string,
  view: SessionArtifactView,
  options: {
    requestScope?: string;
    onPreview?: (session: MyChronSessionExport) => void;
    previewOnly?: boolean;
  } = {},
): Promise<MyChronSessionExport | null> {
  const scope = options.requestScope ?? `session:${sessionId}`;
  activeRequests.get(scope)?.abort();
  const controller = new AbortController();
  activeRequests.set(scope, controller);
  try {
    let manifest: SessionArtifactManifestApiResponse;
    try {
      manifest = await fetchManifest(sessionId, controller.signal);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
    if (!manifest.available) return null;
    const groupDescriptors = view === 'traces'
      ? manifest.artifacts.channelGroups
      : manifest.artifacts.channelGroups.filter((group) => group.groupId === 'common');
    const [overview, laps, geometry] = await Promise.all([
      fetchArtifact<SessionOverviewArtifactV2>(manifest.artifacts.overview, controller.signal),
      fetchArtifact<SessionLapsArtifactV2>(manifest.artifacts.laps, controller.signal),
      manifest.artifacts.geometry
        ? fetchArtifact<SessionGeometryArtifactV2>(manifest.artifacts.geometry, controller.signal)
        : Promise.resolve(null),
    ]);
    if (controller.signal.aborted) throw new axios.CanceledError();
    const geometryPreview = assembleSession(overview, laps, geometry, []);
    options.onPreview?.(geometryPreview);
    if (groupDescriptors.length === 0) return geometryPreview;

    const previewGroups = await Promise.all(
      groupDescriptors.map((group) => fetchArtifact<SessionChannelGroupArtifactV2>(group.preview, controller.signal)),
    );
    if (controller.signal.aborted) throw new axios.CanceledError();
    const tracePreview = assembleSession(overview, laps, geometry, previewGroups);
    options.onPreview?.(tracePreview);
    if (options.previewOnly) return tracePreview;

    const fullGroups = await Promise.all(
      groupDescriptors.map((group) => fetchArtifact<SessionChannelGroupArtifactV2>(group.full, controller.signal)),
    );
    if (controller.signal.aborted) throw new axios.CanceledError();
    return assembleSession(overview, laps, geometry, fullGroups);
  } finally {
    if (activeRequests.get(scope) === controller) activeRequests.delete(scope);
  }
}

export async function prefetchSessionArtifacts(sessionId: string) {
  const inFlight = prefetchRequests.get(sessionId);
  if (inFlight) return inFlight;
  const request = fetchSessionArtifactExport(sessionId, 'map', {
    requestScope: `prefetch:${sessionId}`,
    previewOnly: true,
  }).then(() => undefined).finally(() => {
    prefetchRequests.delete(sessionId);
  });
  prefetchRequests.set(sessionId, request);
  return request;
}

export function clearSessionArtifactCache() {
  activeRequests.forEach((controller) => controller.abort());
  activeRequests.clear();
  artifactRequests.forEach((request) => request.controller.abort());
  manifestRequests.forEach((request) => request.controller.abort());
  artifactRequests.clear();
  manifestRequests.clear();
  prefetchRequests.clear();
  manifestMemory.clear();
  artifactMemory.clear();
}

